// Notifications Panel - AgentBheem ERP
import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useNotifications, useMarkNotificationRead } from '@/hooks/useNotifications'
import {
  Bell,
  BellRing,
  Check,
  AlertCircle,
  Info,
  Calendar,
  X,
  Loader2,
  ExternalLink,
  ChevronRight,
} from 'lucide-react'
import type { Notification } from '@/services/notificationService'

// ============================================
// ENTITY NAVIGATION HELPER
// ============================================

interface EntityNavigationData {
  entity_type?: string
  entity_id?: string
  project_id?: string
  task_id?: string
  milestone_id?: string
  sprint_id?: string
  url?: string
}

/**
 * Get navigation URL based on notification data
 * Supports: projects, tasks, milestones, sprints, clients, alerts
 */
function getEntityNavigationUrl(notification: Notification): string | null {
  const extraData = notification.extra_data as EntityNavigationData | undefined

  // Direct URL provided in extra_data
  if (extraData?.url) {
    return extraData.url
  }

  // Entity type + ID based navigation
  if (extraData?.entity_type && extraData?.entity_id) {
    const entityType = extraData.entity_type.toLowerCase()
    const entityId = extraData.entity_id

    switch (entityType) {
      case 'project':
        return `/dashboard/pm/projects/${entityId}`
      case 'task':
        return `/dashboard/pm/tasks/${entityId}`
      case 'milestone':
        return `/dashboard/pm/milestones/${entityId}`
      case 'sprint':
        return `/dashboard/pm/sprints/${entityId}`
      case 'client':
        return `/dashboard/pm/clients/${entityId}`
      case 'calendar':
      case 'event':
        return `/dashboard/pm/calendar`
      default:
        return null
    }
  }

  // Fallback: Check for specific ID fields in extra_data
  if (extraData?.project_id) {
    return `/dashboard/pm/projects/${extraData.project_id}`
  }
  if (extraData?.task_id) {
    return `/dashboard/pm/tasks/${extraData.task_id}`
  }
  if (extraData?.milestone_id) {
    return `/dashboard/pm/milestones/${extraData.milestone_id}`
  }
  if (extraData?.sprint_id) {
    return `/dashboard/pm/sprints/${extraData.sprint_id}`
  }

  // Fallback: Use alert_id or client_id from main notification
  if (notification.alert_id) {
    return `/dashboard/alerts/${notification.alert_id}`
  }
  if (notification.client_id) {
    return `/dashboard/pm/clients/${notification.client_id}`
  }

  // No navigation available
  return null
}

interface NotificationsPanelProps {
  isOpen: boolean
  onClose: () => void
}

function NotificationIcon({ type }: { type: Notification['notification_type'] }) {
  switch (type) {
    case 'alert':
      return <AlertCircle className="h-4 w-4 text-red-400" />
    case 'reminder':
      return <Calendar className="h-4 w-4 text-yellow-400" />
    case 'report':
      return <Info className="h-4 w-4 text-blue-400" />
    case 'system':
    default:
      return <BellRing className="h-4 w-4 text-emerald-400" />
  }
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function NotificationItem({
  notification,
  onMarkRead,
  onNavigate,
}: {
  notification: Notification
  onMarkRead: (id: string) => void
  onNavigate: (notification: Notification) => void
}) {
  const isUnread = notification.status === 'delivered' || notification.status === 'sent'
  const navigationUrl = getEntityNavigationUrl(notification)
  const isClickable = !!navigationUrl

  const handleClick = () => {
    if (isClickable) {
      // Mark as read when navigating
      if (isUnread) {
        onMarkRead(notification.id)
      }
      onNavigate(notification)
    }
  }

  return (
    <div
      onClick={handleClick}
      className={cn(
        'group relative flex gap-3 p-4 transition-colors',
        'hover:bg-[var(--color-bg-card-hover)]',
        isUnread && 'bg-emerald-500/5',
        isClickable && 'cursor-pointer'
      )}
    >
      {/* Unread indicator */}
      {isUnread && (
        <div className="absolute left-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-emerald-500 rounded-full" />
      )}

      {/* Icon */}
      <div className="flex-shrink-0 w-8 h-8 rounded bg-[var(--color-bg-input)] flex items-center justify-center">
        <NotificationIcon type={notification.notification_type} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm leading-snug', isUnread ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]')}>
          {notification.subject || notification.message || 'New notification'}
        </p>
        {notification.message && notification.subject && (
          <p className="text-xs text-[var(--color-text-muted)] mt-1 line-clamp-2">{notification.message}</p>
        )}
        <p className="text-xs text-[var(--color-text-muted)] mt-1">{formatTimeAgo(notification.created_at)}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        {isUnread && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onMarkRead(notification.id)
            }}
            className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-1 rounded hover:bg-[var(--color-bg-card-hover)] transition-all"
            title="Mark as read"
          >
            <Check className="h-4 w-4 text-[var(--color-text-muted)] hover:text-green-400" />
          </button>
        )}
        {isClickable && (
          <ChevronRight className="h-4 w-4 text-[var(--color-text-muted)] opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
    </div>
  )
}

export function NotificationsPanel({ isOpen, onClose }: NotificationsPanelProps) {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<'all' | 'unread'>('all')
  const { data, isLoading, error } = useNotifications({
    page: 1,
    page_size: 20,
    status: filter === 'unread' ? 'delivered' : undefined,
  })
  const markReadMutation = useMarkNotificationRead()

  // Handle navigation to related entity
  const handleNavigate = useCallback((notification: Notification) => {
    const url = getEntityNavigationUrl(notification)
    if (url) {
      onClose() // Close panel before navigating
      navigate(url)
    }
  }, [navigate, onClose])

  if (!isOpen) return null

  const notifications = data?.items || []
  const unreadCount = notifications.filter(
    (n) => n.status === 'delivered' || n.status === 'sent'
  ).length

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />

      {/* Panel */}
      <div
        className={cn(
          'fixed right-4 top-[78px] w-96 max-h-[70vh]',
          'bg-[var(--color-bg-dropdown)] border border-[var(--color-border-hover)] rounded-xl',
          'shadow-2xl shadow-black/50',
          'z-[9999] overflow-hidden',
          'animate-fade-in-up'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-emerald-500" />
            <h3 className="font-semibold text-[var(--color-text-primary)]">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-500 text-xs rounded-full">
                {unreadCount} new
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--color-bg-card-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-1 p-2 border-b border-[var(--color-border)]">
          <button
            onClick={() => setFilter('all')}
            className={cn(
              'px-3 py-1.5 text-xs rounded transition-colors',
              filter === 'all'
                ? 'bg-emerald-500/20 text-emerald-500'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]'
            )}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={cn(
              'px-3 py-1.5 text-xs rounded transition-colors',
              filter === 'unread'
                ? 'bg-emerald-500/20 text-emerald-500'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card-hover)]'
            )}
          >
            Unread
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(70vh-120px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
            </div>
          ) : error ? (
            <div className="text-center py-12 px-4">
              <Bell className="h-10 w-10 text-[var(--color-text-muted)] mx-auto mb-3" />
              <p className="text-sm text-[var(--color-text-secondary)]">No notifications available</p>
              <p className="text-xs text-[var(--color-text-muted)] mt-1">Check back later for updates</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Bell className="h-10 w-10 text-[var(--color-text-muted)] mx-auto mb-3" />
              <p className="text-sm text-[var(--color-text-secondary)]">No notifications</p>
              <p className="text-xs text-[var(--color-text-muted)] mt-1">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--color-border)]">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={(id) => markReadMutation.mutate(id)}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-[var(--color-border)]">
            <button
              onClick={() => {
                onClose()
                navigate('/dashboard/notifications')
              }}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-emerald-500 hover:text-emerald-400 transition-colors"
            >
              View all notifications
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </>
  )
}
